import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { MyShieldConnectionCard } from '@/components/MyShieldConnectionCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Database, RefreshCw, CheckCircle2, Upload, Clock, AlertCircle, Shield, Globe, Users, Server, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function SettingsPage({ user, onLogout }) {
  const isSuperAdmin = user?.role === 'super_admin';
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [watchlistCount, setWatchlistCount] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [uploading, setUploading] = useState({ ofac: false, moha: false, pep: false, unscr: false });

  useEffect(() => {
    fetchWatchlistCount();
    fetchSyncLogs();
  }, []);

  const fetchWatchlistCount = async () => {
    try {
      const response = await axios.get(`${API}/watchlist/count`, { headers: getAuthHeaders() });
      setWatchlistCount(response.data);
    } catch (error) {
      toast.error('Failed to load watchlist count');
    }
  };

  const fetchSyncLogs = async () => {
    try {
      const response = await axios.get(`${API}/watchlist/sync-logs`, { headers: getAuthHeaders() });
      setSyncLogs(response.data);
    } catch (error) {
      console.error('Failed to load sync logs');
    }
  };

  const syncWatchlists = async () => {
    setSyncing(true);
    setSyncStatus({ status: 'starting', message: 'Initializing sync...' });
    try {
      const response = await axios.post(`${API}/watchlist/sync`, {}, { headers: getAuthHeaders() });
      const syncId = response.data.sync_id;
      toast.info('Sync started - fetching OFAC, MY MOHA and OpenSanctions data...');
      const pollStatus = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/watchlist/sync-status/${syncId}`, { headers: getAuthHeaders() });
          const status = statusRes.data;
          setSyncStatus(status);
          if (status.status === 'completed') {
            clearInterval(pollStatus);
            toast.success(`Sync completed! ${status.ofac_entries || 0} OFAC, ${status.opensanctions_entries || 0} OpenSanctions entries`);
            setSyncing(false);
            setSyncStatus(null);
            fetchWatchlistCount();
            fetchSyncLogs();
          } else if (status.status === 'failed') {
            clearInterval(pollStatus);
            toast.error(`Sync failed: ${status.message}`);
            setSyncing(false);
            setSyncStatus(null);
          }
        } catch (error) {
          console.error('Error polling sync status:', error);
        }
      }, 2000);
    } catch (error) {
      toast.error('Failed to start sync');
      setSyncing(false);
      setSyncStatus(null);
    }
  };

  const handleUpload = async (source, accept, endpoint, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, [source]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}${endpoint}`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      toast.success(res.data.message || `${source} upload complete`);
      fetchWatchlistCount();
      fetchSyncLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || `${source} upload failed`);
    } finally {
      setUploading(prev => ({ ...prev, [source]: false }));
      e.target.value = '';
    }
  };

  const stat = (label, value, icon, color) => (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="text-2xl font-bold text-slate-900 mt-1">
        {value != null ? value.toLocaleString() : '-'}
      </p>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout} activePage="settings">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="settings-title">Settings</h1>
          <p className="text-sm text-slate-600 mt-2">Manage watchlist data and system configuration</p>
        </div>

        {/* Watchlist Management */}
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center">
              <Database className="w-5 h-5 text-slate-600 mr-2" />
              <h2 className="text-xl font-semibold text-slate-900">Watchlist Management</h2>
            </div>
          </div>
          <div className="p-6 space-y-6">
            {watchlistCount && (
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 p-4 bg-slate-50 rounded-md" data-testid="watchlist-stats">
                {stat('Total', watchlistCount.total, <Database className="w-3 h-3" />, 'slate')}
                {stat('OFAC SDN', watchlistCount.ofac, <Server className="w-3 h-3" />, 'red')}
                {stat('MY MOHA', watchlistCount.my_moha, <Shield className="w-3 h-3" />, 'amber')}
                {stat('PEP', watchlistCount.opensanctions_pep, <Users className="w-3 h-3" />, 'blue')}
                {stat('UNSCR', watchlistCount.unscr, <AlertCircle className="w-3 h-3" />, 'orange')}
                {stat('EU', watchlistCount.eu_consolidated, <Shield className="w-3 h-3" />, 'indigo')}
                {stat('OpenSanctions', watchlistCount.opensanctions, <Globe className="w-3 h-3" />, 'purple')}
              </div>
            )}

            <div className="space-y-3">
              {isSuperAdmin ? (
              <>
              <Button
                onClick={syncWatchlists}
                disabled={syncing}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="sync-watchlists-button"
              >
                {syncing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Syncing...</> : <><RefreshCw className="w-4 h-4 mr-2" />Sync All Watchlists</>}
              </Button>
              <p className="text-xs text-slate-500">
                Fetches latest data from OFAC SDN, MY MOHA Sanctions, OpenSanctions PEP, and UNSCR
              </p>
              </>
              ) : (
              <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-md border border-amber-200">
                Only Super Admin can sync watchlists. Contact your administrator for data updates.
              </p>
              )}

              {syncStatus && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md" data-testid="sync-progress">
                  <div className="flex items-center">
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">{syncStatus.message}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-start p-4 bg-blue-50 border border-blue-200 rounded-md">
              <CheckCircle2 className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium mb-1">Automatic Updates</p>
                <p>All watchlists (OFAC SDN, MY MOHA, PEP, UNSCR) are automatically synced daily at 2:00 AM.</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Source-Specific File Uploads - Super Admin Only */}
        {isSuperAdmin && (
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center">
              <Upload className="w-5 h-5 text-slate-600 mr-2" />
              <h2 className="text-xl font-semibold text-slate-900">Manual File Upload</h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">Upload watchlist files manually for each data source</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* OFAC Upload */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-slate-900">OFAC SDN</h3>
                </div>
                <p className="text-xs text-slate-500">Upload SDN_ENHANCED.XML from the U.S. Treasury OFAC website</p>
                <label className="block cursor-pointer">
                  <input type="file" accept=".xml" className="hidden" data-testid="ofac-upload-input"
                    onChange={(e) => handleUpload('ofac', '.xml', '/watchlist/upload/ofac', e)}
                    disabled={uploading.ofac} />
                  <div className={`flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-dashed rounded-md text-sm transition-colors ${uploading.ofac ? 'border-slate-300 text-slate-400' : 'border-red-300 text-red-700 hover:bg-red-50'}`}>
                    {uploading.ofac ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading.ofac ? 'Uploading...' : 'Upload XML'}
                  </div>
                </label>
                <a href="https://sanctionslistservice.ofac.treas.gov/api/download/SDN_ENHANCED.XML" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">Download from OFAC</a>
              </div>

              {/* MY MOHA Upload */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  <h3 className="font-semibold text-slate-900">MY MOHA</h3>
                </div>
                <p className="text-xs text-slate-500">Upload entities.ftm.json from OpenSanctions Malaysia MOHA dataset</p>
                <label className="block cursor-pointer">
                  <input type="file" accept=".json" className="hidden" data-testid="moha-upload-input"
                    onChange={(e) => handleUpload('moha', '.json', '/watchlist/upload/my-moha', e)}
                    disabled={uploading.moha} />
                  <div className={`flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-dashed rounded-md text-sm transition-colors ${uploading.moha ? 'border-slate-300 text-slate-400' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
                    {uploading.moha ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading.moha ? 'Uploading...' : 'Upload JSON'}
                  </div>
                </label>
                <a href="https://www.opensanctions.org/datasets/my_moha_sanctions/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">Download from OpenSanctions</a>
              </div>

              {/* PEP Upload */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-slate-900">OpenSanctions PEP</h3>
                </div>
                <p className="text-xs text-slate-500">Upload entities.ftm.json or CSV from the OpenSanctions PEP dataset</p>
                <label className="block cursor-pointer">
                  <input type="file" accept=".json,.csv,.txt" className="hidden" data-testid="pep-upload-input"
                    onChange={(e) => handleUpload('pep', '.json,.csv', '/watchlist/upload', e)}
                    disabled={uploading.pep} />
                  <div className={`flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-dashed rounded-md text-sm transition-colors ${uploading.pep ? 'border-slate-300 text-slate-400' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}>
                    {uploading.pep ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading.pep ? 'Uploading...' : 'Upload File'}
                  </div>
                </label>
                <a href="https://www.opensanctions.org/datasets/peps/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">Download from OpenSanctions</a>
              </div>

              {/* UNSCR Upload */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-slate-900">UNSCR</h3>
                </div>
                <p className="text-xs text-slate-500">Upload targets.nested.json from the UN Security Council Sanctions dataset</p>
                <label className="block cursor-pointer">
                  <input type="file" accept=".json" className="hidden" data-testid="unscr-upload-input"
                    onChange={(e) => handleUpload('unscr', '.json', '/watchlist/upload/unscr', e)}
                    disabled={uploading.unscr} />
                  <div className={`flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-dashed rounded-md text-sm transition-colors ${uploading.unscr ? 'border-slate-300 text-slate-400' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`}>
                    {uploading.unscr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading.unscr ? 'Uploading...' : 'Upload JSON'}
                  </div>
                </label>
                <a href="https://www.opensanctions.org/datasets/un_sc_sanctions/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">Download from OpenSanctions</a>
              </div>

              {/* EU Consolidated Upload */}
              <div className="p-4 border border-slate-200 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-500" />
                  <h3 className="font-semibold text-slate-900">EU Consolidated</h3>
                </div>
                <p className="text-xs text-slate-500">Upload entities.ftm.json from the EU Financial Sanctions Files dataset</p>
                <label className="block cursor-pointer">
                  <input type="file" accept=".json" className="hidden" data-testid="eu-upload-input"
                    onChange={(e) => handleUpload('eu', '.json', '/watchlist/upload/eu-fsf', e)}
                    disabled={uploading.eu} />
                  <div className={`flex items-center justify-center gap-2 w-full px-3 py-2.5 border border-dashed rounded-md text-sm transition-colors ${uploading.eu ? 'border-slate-300 text-slate-400' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'}`}>
                    {uploading.eu ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    {uploading.eu ? 'Uploading...' : 'Upload JSON'}
                  </div>
                </label>
                <a href="https://www.opensanctions.org/datasets/eu_fsf/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline block">Download from OpenSanctions</a>
              </div>
            </div>
          </div>
        </Card>
        )}

        {/* Adverse-Media Connection - Super Admin Only */}
        {isSuperAdmin && <MyShieldConnectionCard />}

        {/* Sync History */}
        {syncLogs.length > 0 && (
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center">
                <Clock className="w-5 h-5 text-slate-600 mr-2" />
                <h2 className="text-xl font-semibold text-slate-900">Sync History</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Date/Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Source</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Entries</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {syncLogs.slice(0, 15).map((log, idx) => (
                    <tr key={log.id || log._id || `${log.source}-${log.timestamp || idx}`} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-mono text-xs text-slate-600">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="px-6 py-4 text-slate-900">{log.source}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                          {log.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{(log.entries_synced || log.entries_enriched || (log.ofac_entries || 0) + (log.opensanctions_entries || 0) || 0).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        {log.status === 'success' || log.status === 'completed' ? (
                          <span className="text-emerald-600 flex items-center">
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            {log.status === 'completed' && (log.entries_synced || log.entries_enriched || 0) === 0 ? 'No entries to process' : 'Success'}
                          </span>
                        ) : log.status === 'in_progress' ? (
                          <span className="text-blue-600 flex items-center">
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                            In Progress
                          </span>
                        ) : log.status === 'stopped' ? (
                          <span className="text-amber-600 flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            Stopped
                          </span>
                        ) : (
                          <span className="text-rose-600 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Data Sources */}
        <Card className="border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900">Data Sources</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-start">
              <div className="w-10 h-10 bg-red-50 rounded-md flex items-center justify-center mr-4 shrink-0">
                <Server className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">OFAC SDN List</h3>
                <p className="text-sm text-slate-600 mt-1">Office of Foreign Assets Control - Specially Designated Nationals (SDN_ENHANCED.XML)</p>
                <a href="https://ofac.treasury.gov/sanctions-list-service" target="_blank" rel="noopener noreferrer" data-testid="ofac-official-source-link" className="text-xs text-blue-600 hover:underline mt-1 inline-block">View Official API</a>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-amber-50 rounded-md flex items-center justify-center mr-4 shrink-0">
                <Shield className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Malaysia MOHA Sanctions</h3>
                <p className="text-sm text-slate-600 mt-1">Ministry of Home Affairs (Kementerian Dalam Negeri) - Designated entities under Malaysian law</p>
                <a href="https://www.opensanctions.org/datasets/my_moha_sanctions/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">View Source</a>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center mr-4 shrink-0">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">OpenSanctions PEP</h3>
                <p className="text-sm text-slate-600 mt-1">Politically Exposed Persons - Global database of sanctions and PEP entities</p>
                <a href="https://www.opensanctions.org/datasets/peps/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">View Source</a>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-orange-50 rounded-md flex items-center justify-center mr-4 shrink-0">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">UN Security Council Sanctions (UNSCR)</h3>
                <p className="text-sm text-slate-600 mt-1">Individuals and entities designated by the UN Security Council under various resolutions (1267, 1718, 1988, etc.)</p>
                <a href="https://www.opensanctions.org/datasets/un_sc_sanctions/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">View Source</a>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-10 h-10 bg-indigo-50 rounded-md flex items-center justify-center mr-4 shrink-0">
                <Shield className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">EU Consolidated (FSF)</h3>
                <p className="text-sm text-slate-600 mt-1">Persons, groups and entities subject to EU financial sanctions and travel bans, published by the EU Commission DG FISMA.</p>
                <a href="https://www.opensanctions.org/datasets/eu_fsf/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block">View Source</a>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
